import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { getAllProducts, getProductById, Product } from '../utils/productManager';
import SEO from './SEO';

const ProductDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [product, setProduct] = useState<Product | undefined>(undefined);
    const [recommended, setRecommended] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    // Fetch product data
    const [activeImage, setActiveImage] = useState<string>('');

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            if (id) {
                const found = await getProductById(Number(id));
                setProduct(found);
                if (found) {
                    setActiveImage(found.image || (found.images && found.images[0]) || '');
                }

                if (found) {
                    const all = await getAllProducts();
                    setRecommended(all.filter(p => p.id !== found.id).slice(0, 3));
                }
            }
            setLoading(false);
        };
        fetchProduct();
    }, [id]);

    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Scroll to top when ID changes
    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    useGSAP(() => {
        if (!product) return;

        const tl = gsap.timeline();

        // Image reveal
        if (imageRef.current) {
            tl.from(imageRef.current, {
                clipPath: 'inset(0% 100% 0% 0%)',
                autoAlpha: 0,
                duration: 1.5,
                ease: 'power4.out'
            });
        }

        // Content fade up
        if (contentRef.current) {
            tl.from(contentRef.current.children, {
                y: 30,
                autoAlpha: 0,
                duration: 1,
                stagger: 0.1,
                ease: 'power3.out'
            }, "-=1");
        }

        // Recommended products animation
        const cards = gsap.utils.toArray<HTMLElement>('.recommended-card');
        if (cards.length > 0) {
            gsap.from(cards, {
                y: 50,
                opacity: 0,
                duration: 0.8,
                stagger: 0.1,
                scrollTrigger: {
                    trigger: '.recommended-section',
                    start: 'top 80%',
                }
            });
        }

    }, { scope: containerRef, dependencies: [id] }); /* Re-run GSAP when ID changes */

    if (loading) {
        return (
            <div className="min-h-screen bg-diwan-900 flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-diwan-gold border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-diwan-900 flex items-center justify-center text-diwan-cream">
                <h2 className="text-2xl font-serif">Product not found.</h2>
            </div>
        );
    }

    // Recommended logic moved to effect

    return (
        <div ref={containerRef} className="min-h-screen bg-white text-diwan-900 selection:bg-diwan-gold selection:text-white">
            <SEO
                title={`${product.name} - Luxury Furniture`}
                description={product.description?.substring(0, 160)}
                image={activeImage}
                url={`/products/${product.id}`}
                type="product"
            />
            {/* Navbar Placeholder (Assuming Header is fixed/absolute) */}
            <div className="h-20 bg-diwan-900"></div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
                <div className="flex flex-col md:flex-row gap-16 lg:gap-24">

                    {/* Product Image Gallery */}
                    <div ref={imageRef} className="w-full md:w-1/2 relative space-y-4">
                        <div className="aspect-[4/5] w-full overflow-hidden bg-gray-50 rounded-2xl shadow-sm border border-gray-100 relative group">
                            <img
                                src={activeImage}
                                alt={product.name}
                                className="w-full h-full object-contain p-8 transition-transform duration-500 ease-in-out group-hover:scale-105"
                            />
                        </div>
                        {/* Thumbnails */}
                        {product.images && product.images.length > 1 && (
                            <div className="flex gap-4 overflow-x-auto pb-2">
                                {product.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImage(img)}
                                        className={`w-20 h-20 flex-shrink-0 bg-white rounded-lg border-2 p-2 transition-all ${activeImage === img ? 'border-diwan-gold' : 'border-gray-100 hover:border-gray-300'}`}
                                    >
                                        <img src={img} alt={`View ${idx}`} className="w-full h-full object-contain" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div ref={contentRef} className="w-full md:w-1/2 flex flex-col justify-center space-y-8">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium mb-4 text-black">
                                {product.name}
                            </h1>
                            <div className="h-[2px] w-20 bg-diwan-gold mb-6"></div>
                        </div>

                        <p className="text-gray-600 leading-relaxed text-lg font-light">
                            {product.description}
                        </p>

                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm pt-4 border-t border-gray-200">
                            <div className="col-span-2 md:col-span-1">
                                <span className="block text-gray-400 uppercase tracking-widest text-xs mb-1">Category</span>
                                <span className="font-medium text-black">{product.category}</span>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <span className="block text-gray-400 uppercase tracking-widest text-xs mb-1">Code</span>
                                <span className="font-medium text-black">{product.code || 'N/A'}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="block text-gray-400 uppercase tracking-widest text-xs mb-1">Materials</span>
                                <span className="font-medium text-black">{product.material || 'N/A'}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="block text-gray-400 uppercase tracking-widest text-xs mb-1">Dimensions</span>
                                <span className="font-medium text-black">{product.dimensions || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="flex gap-4 pt-6">
                            <span className="text-gray-400 uppercase tracking-widest text-xs self-center">Share: </span>
                            <button className="hover:text-diwan-gold transition-colors text-black">
                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" /></svg>
                            </button>
                            <button className="hover:text-diwan-gold transition-colors text-black">
                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>
                            </button>
                        </div>

                        <div className="pt-8">
                            <Link to="/contact" className="block w-full">
                                <button className="w-full bg-black text-white py-4 hover:bg-diwan-gold transition-colors duration-300 uppercase tracking-[0.2em] text-xs font-bold">
                                    Contact Us
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Recommended Products */}
                <div className="mt-24 recommended-section">
                    <h3 className="text-3xl font-serif text-center mb-16 text-black">Related Products</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {recommended.map((item) => (
                            <Link to={`/products/${item.id}`} key={item.id} className="recommended-card group block">
                                <div className="aspect-square bg-gray-50 overflow-hidden mb-6 relative">
                                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-300 z-10"></div>
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                                    />
                                </div>
                                <h4 className="text-xl font-serif text-black group-hover:text-diwan-gold transition-colors mb-2">{item.name}</h4>
                                <span className="text-xs uppercase tracking-widest text-gray-500">{item.category}</span>
                            </Link>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProductDetails;
